import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Clock } from "lucide-react";
import { getCheckoutStatus } from "@/lib/api";
import { useCart, formatNZD } from "@/lib/cart";

const POLL_INTERVAL = 2500;
const MAX_POLLS = 10;

export default function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ state: "loading" });
  const { clear } = useCart();
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus({ state: "error", message: "Missing checkout session." });
      return;
    }

    let attempts = 0;
    let timer = null;
    let stopped = false;

    const poll = async () => {
      attempts += 1;
      try {
        const res = await getCheckoutStatus(sessionId);
        if (res.payment_status === "paid") {
          if (!clearedRef.current) {
            clearedRef.current = true;
            clear();
          }
          setStatus({ state: "paid", ...res });
          return;
        }
        if (res.status === "expired" || res.payment_status === "failed") {
          setStatus({ state: "failed", ...res });
          return;
        }
        if (attempts >= MAX_POLLS) {
          setStatus({ state: "pending", ...res });
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (attempts >= MAX_POLLS || stopped) {
          setStatus({ state: "error", message: "Could not verify payment." });
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL);
      }
    };
    poll();

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div data-testid="checkout-success-page" className="max-w-[800px] mx-auto px-6 md:px-12 pt-24 pb-24 text-center">
      {status.state === "loading" && (
        <div data-testid="checkout-loading">
          <Loader2 size={28} className="mx-auto mb-6 animate-spin text-[#6F4E37]" />
          <h1 className="font-serif text-4xl md:text-5xl">Confirming your payment…</h1>
          <p className="mt-4 text-[#6F4E37]">Hang tight, just a few seconds.</p>
        </div>
      )}

      {status.state === "paid" && (
        <div data-testid="checkout-paid">
          <CheckCircle2 size={36} className="mx-auto mb-6 text-[#4A5D4E]" />
          <h1 className="font-serif text-5xl md:text-6xl tracking-tight">Thank you.</h1>
          <p className="mt-6 text-[#2C2421]/80 leading-relaxed text-lg">
            Your payment of <strong className="font-medium">{formatNZD(status.amount)}</strong> {status.currency?.toUpperCase()} was received.
            We'll send a receipt to your email and start your piece in the workshop.
          </p>
          <p
            data-testid="success-junk-note"
            className="mt-5 text-sm text-[#6F4E37] leading-relaxed bg-[#F5F1E8] border border-[#E5DDC9] px-5 py-3 inline-block"
          >
            Can't find our email? Please check your <strong>Junk / Spam</strong> folder —
            new senders sometimes land there for the first few weeks.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/shop"
              data-testid="success-shop-btn"
              className="inline-flex items-center gap-3 bg-[#2C2421] text-[#FDFBF7] px-7 py-4 text-sm font-medium hover:bg-[#4A3C38] transition-colors"
            >
              Back to shop
            </Link>
            <Link
              to="/care"
              data-testid="success-care-btn"
              className="inline-flex items-center gap-3 border border-[#2C2421] text-[#2C2421] px-7 py-4 text-sm font-medium hover:bg-[#2C2421] hover:text-[#FDFBF7] transition-colors"
            >
              Read the care card
            </Link>
          </div>
        </div>
      )}

      {status.state === "pending" && (
        <div data-testid="checkout-pending">
          <Clock size={28} className="mx-auto mb-6 text-[#6F4E37]" />
          <h1 className="font-serif text-4xl md:text-5xl">Payment is processing.</h1>
          <p className="mt-4 text-[#6F4E37]">
            We're still waiting on confirmation from Stripe. You'll get an email
            receipt as soon as it completes — no need to pay again.
          </p>
          <button
            data-testid="success-back-btn"
            onClick={() => navigate("/")}
            className="mt-10 inline-flex items-center gap-3 border border-[#2C2421] px-7 py-4 text-sm font-medium hover:bg-[#2C2421] hover:text-[#FDFBF7] transition-colors"
          >
            Back to home
          </button>
        </div>
      )}

      {(status.state === "failed" || status.state === "error") && (
        <div data-testid="checkout-failed">
          <XCircle size={28} className="mx-auto mb-6 text-[#8a3b3b]" />
          <h1 className="font-serif text-4xl md:text-5xl">Something went wrong.</h1>
          <p className="mt-4 text-[#6F4E37]">
            {status.message || "We couldn't confirm your payment. If you were charged, please email us."}
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/cart"
              data-testid="failed-cart-btn"
              className="inline-flex items-center gap-3 bg-[#2C2421] text-[#FDFBF7] px-7 py-4 text-sm font-medium hover:bg-[#4A3C38] transition-colors"
            >
              Back to cart
            </Link>
            <Link
              to="/contact"
              data-testid="failed-contact-btn"
              className="inline-flex items-center gap-3 border border-[#2C2421] px-7 py-4 text-sm font-medium hover:bg-[#2C2421] hover:text-[#FDFBF7] transition-colors"
            >
              Contact the workshop
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
